import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import { setValueToStore } from '../../../../../sharedStore';
import { buildBillingDetails } from './utils';

function BankAccountSection({
  stripe,
  connectedBankInfo,
  setConnectedBankInfo,
  dataFieldsProps,
}) {
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (window && stripe) {
      if (!_isEmpty(dataFieldsProps.responses)) {
        setValueToStore('billingDetails', buildBillingDetails(dataFieldsProps));
      }
      setValueToStore('connectBankAccount', connectBankAccount);
      setValueToStore('confirmBankAccountPayment', confirmUsBankAccountPayment);
    }
  }, [stripe]);

  const connectBankAccount = async (clientSecret, billingDetails) => {
    return await collectBankDetails(clientSecret, billingDetails);
  };

  const confirmUsBankAccountPayment = async (clientSecret) => {
    return await confirmBankPayment(clientSecret);
  };

  const collectBankDetails = async (clientSecret, billingDetails) => {
    setLoading(true);
    await stripe
      .collectBankAccountForPayment({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: billingDetails,
          },
        },
        expand: ['payment_method'],
      })
      .then(({ paymentIntent, error }) => {
        if (error) {
          // PaymentMethod collection failed for some reason.
        } else if (paymentIntent.status === 'requires_payment_method') {
          // Customer canceled the hosted verification modal. Present them with other
          // payment method type options.
        } else if (paymentIntent.status === 'requires_confirmation') {
          setConnectedBankInfo(paymentIntent.payment_method.us_bank_account);
        }
        setLoading(false);
      });
  };

  const confirmBankPayment = async (clientSecret) => {
    const values = {
      client_secret: clientSecret,
    };

    await stripe
      .confirmUsBankAccountPayment(clientSecret)
      .then(({ paymentIntent, error }) => {
        if (error) {
          // The payment failed for some reason.
          values.error = {
            message: 'There was an error. Please try with another account.',
          };
        } else if (paymentIntent.status === 'requires_payment_method') {
          // Confirmation failed. Attempt again with a different payment method.
          values.error = {
            message: 'There was an error. Please try with another account.',
          };
        } else if (paymentIntent.status === 'processing') {
          values.payment_intent = paymentIntent;
          // Confirmation succeeded! The account will be debited.
          // Display a message to customer.
        } else if (
          paymentIntent.next_action?.type === 'verify_with_microdeposits'
        ) {
          // The account needs to be verified via microdeposits.
          // Display a message to consumer with next steps (consumer waits for
          // microdeposits, then enters a statement descriptor code on a page sent to them via email).
        }
      });

    return values;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        p: 2.5,
        gap: 2,
        border: 1,
        borderRadius: 2,
        borderColor: 'var(--border-color-primary)',
        color: 'var(--font-color-primary)',
        bgcolor: '#fff',
      }}
      data-target="donations.connectAccountCard"
      data-css-selector="connect account card"
    >
      {loading ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress
            size={30}
            sx={{ color: 'rgba(29, 29, 29, 0.38)' }}
          />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{ display: 'flex', color: 'rgba(51, 51, 51, 0.54)', mr: 2 }}
            >
              <AccountBalanceIcon />
            </Box>
            {connectedBankInfo ? (
              <Box
                data-css-selector="connected bank info"
                sx={{ fontSize: 14, mr: 2 }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {connectedBankInfo.bank_name}
                </Box>
                <Box sx={{ fontSize: 12 }}>**** {connectedBankInfo.last4}</Box>
              </Box>
            ) : (
              <Box
                data-css-selector="connected bank info"
                sx={{ color: 'rgba(29, 29, 29, 0.38)', fontSize: 14, mr: 2 }}
              >
                No bank account
              </Box>
            )}
          </Box>
          {!connectedBankInfo && (
            <Box>
              <Button
                sx={{
                  width: 'auto',
                  color: '#fff',
                  backgroundColor: 'var(--primary-color)',
                  m: 0,
                  p: '4px 10px',
                  borderRadius: 2,
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--primary-color-darken)',
                  },
                }}
                disableRipple
                data-action="donations#connectAccount"
                data-target="donations.connectAccount"
              >
                Connect your account
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default BankAccountSection;
